import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Button,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  Pagination,
  Grid,
  Chip,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import AnseraLogo from "assets/images/Anser.png";
import { FAST_API_BASE_URL } from "constants";
import { useDomain } from "context/DomainContext";
import { useAuth } from "context/AuthContext";
import { useSystemTheme } from "context/SystemThemeContext";

const DataSync = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  const apiUrl = FAST_API_BASE_URL;

  const [collections, setCollections] = useState([]);
  const [collectionItems, setCollectionItems] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [pendingItemIds, setPendingItemIds] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentType, setContentType] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const rowsPerPage = itemsPerPage * 2;
  const [syncFilter, setSyncFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  const source = localStorage.getItem("source") || "webflow";

  const refreshCurrentData = useCallback(async () => {
    if (contentType === "page") {
      await fetchPages();
    } else if (contentType === "collections" && selectedCollection) {
      await fetchCollectionItems(selectedCollection);
    }
  }, [contentType, selectedCollection]);

  const fetchCollectionItems = useCallback(async (collectionId) => {
    setLoadingItems(true);
    try {
      const res = await axios.get(`${apiUrl}/api/webflow-collections/${collectionId}`, {
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
      });

      const { result } = res.data;
      const items = result?.result ?? [];
      const synced_ids = result?.synced_ids ?? [];
      const pending_ids = result?.pending_ids ?? [];

      setCollectionItems(items);
      setSelectedItemIds(synced_ids);
      setPendingItemIds(pending_ids);
    } catch (err) {
      console.error("Error fetching collection items", err.response?.data || err.message);
    } finally {
      setLoadingItems(false);
    }
  }, [apiUrl, domainId]);

  useEffect(() => {
    if (source === "webflow" && contentType === "collections") {
      const fetchCollections = async () => {
        try {
          const res = await axios.get(`${apiUrl}/api/webflow-collections`, {
            headers: {
              "DOMAIN-TOKEN": domainId,
              "Content-Type": "application/json",
            },
          });
          setCollections(res.data.result);
        } catch (err) {
          console.error("Error fetching collections", err.response?.data || err.message);
        }
      };
      fetchCollections();
    }
  }, [apiUrl, contentType, domainId, source]);

  const handleCollectionChange = useCallback(
    async (value) => {
      setSelectedCollection(value);
      setSelectedItemIds([]);
      setPendingItemIds([]);
      setCollectionItems([]);
      setLoadingItems(true);
      setSyncFilter("all");
      setPage(1);
      setSearchQuery("");
      await fetchCollectionItems(value);
    },
    [fetchCollectionItems]
  );

  const fetchPages = useCallback(async () => {
    setLoadingItems(true);
    try {
      let res;
      if (source !== "webflow" && source !== "wordpress") {
        res = await axios.get(`${apiUrl}/api/custom-pages`, {
          headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
        });
      } else {
        res = await axios.get(`${apiUrl}/api/webflow-pages`, {
          headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
        });
      }

      const pagesData = res.data.result?.result ?? [];
      const synced_ids = res.data.result?.synced_ids ?? [];
      const pending_ids = res.data.result?.pending_ids ?? [];

      setCollectionItems(pagesData);
      setSelectedItemIds(synced_ids);
      setPendingItemIds(pending_ids);
      setPage(1);
      setSyncFilter("all");
    } catch (err) {
      console.error("Error fetching pages", err.response?.data || err.message);
    } finally {
      setLoadingItems(false);
    }
  }, [apiUrl, domainId, source]);

  const handleContentTypeChange = (value) => {
    setContentType(value);
    setSelectedCollection(null);
    setSelectedItemIds([]);
    setPendingItemIds([]);
    setCollectionItems([]);
    setSyncFilter("all");
    setPage(1);
    setSearchQuery("");

    if (value === "page") {
      fetchPages();
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setPage(1);
  };

  useEffect(() => {
    if (source === "custom") {
      setContentType("page");
    }
    fetchPages();
  }, [domainId, source, fetchPages]);

  const handleItemCheck = (id) => {
    if (pendingItemIds.includes(id)) return;
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      if (!domainId) {
        setSnackbarMessage("Domain ID not found.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setSaving(false);
        return;
      }

      if (source !== "webflow" && source !== "wordpress" && contentType === "page") {
        const res = await axios.get(`${apiUrl}/api/custom-pages`, {
          headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
        });

        const currentSyncedIds = res.data.result?.synced_ids || [];
        const newSelectedIds = selectedItemIds.filter((id) => !currentSyncedIds.includes(id));
        const deselectedIds = currentSyncedIds.filter((id) => !selectedItemIds.includes(id));

        if (deselectedIds.length > 0) {
          await axios.delete(`${apiUrl}/api/unsync/content`, {
            data: { type: "page", item_ids: deselectedIds.map((id) => ({ id })) },
            headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
          });
          setSnackbarMessage("Pages unsynced successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          await refreshCurrentData();
        }

        if (newSelectedIds.length > 0) {
          const syncPayload = collectionItems
            .filter((item) => newSelectedIds.includes(item.id))
            .map((item) => ({ id: item.id, title: item.title, url: item.url }));

          await axios.post(`${apiUrl}/api/sync-custom-pages`, syncPayload, {
            headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
          });
          setSnackbarMessage("Custom pages synced successfully!");
          window.dispatchEvent(new CustomEvent("syncStarted"));
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          await refreshCurrentData();
        }

        if (deselectedIds.length === 0 && newSelectedIds.length === 0) {
          setSnackbarMessage("No changes to save.");
          setSnackbarSeverity("info");
          setSnackbarOpen(true);
        }

        setSaving(false);
        return;
      }

      let apiEndpoint;
      let syncEndpoint = `${apiUrl}/api/sync/content`;

      if (contentType === "collections" && selectedCollection) {
        apiEndpoint = `${apiUrl}/api/webflow-collections/${selectedCollection}`;
      } else {
        apiEndpoint = `${apiUrl}/api/webflow-pages`;
      }

      const res = await axios.get(apiEndpoint, {
        headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
        params: contentType === "collections" ? {} : { type: contentType },
      });

      const currentSyncedIds = res.data.result?.synced_ids || res.data.synced_ids || [];
      const newSelectedIds = selectedItemIds.filter((id) => !currentSyncedIds.includes(id));
      const deselectedIds = currentSyncedIds.filter((id) => !selectedItemIds.includes(id));

      let hasChanges = false;

      if (deselectedIds.length > 0) {
        const unsyncPayload = {
          type: contentType,
          item_ids: deselectedIds.map((id) => ({ id })),
          ...(contentType === "collections" && selectedCollection ? { collection_id: selectedCollection } : {}),
        };

        await axios.delete(`${apiUrl}/api/unsync/content`, {
          data: unsyncPayload,
          headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
        });
        setSnackbarMessage("Items unsynced successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        hasChanges = true;
      }

      if (newSelectedIds.length > 0) {
        let syncPayload;

        if (contentType === "collections" && selectedCollection) {
          syncPayload = {
            type: contentType,
            collection_id: selectedCollection,
            item_ids: collectionItems
              .filter((item) => newSelectedIds.includes(item.id))
              .map((item) => ({ id: item.id, title: item.title })),
          };
        } else {
          syncPayload = {
            type: contentType,
            item_ids: newSelectedIds.map((id) => ({ id })),
          };
        }

        await axios.post(syncEndpoint, syncPayload, {
          headers: { "DOMAIN-TOKEN": domainId, "Content-Type": "application/json" },
        });
        setSnackbarMessage("Items synced successfully!");
        window.dispatchEvent(new CustomEvent("syncStarted"));
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        hasChanges = true;
      }

      if (deselectedIds.length === 0 && newSelectedIds.length === 0) {
        setSnackbarMessage("No changes to save.");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }

      if (hasChanges) await refreshCurrentData();
    } catch (error) {
      console.error("Error saving changes:", error);
      setSnackbarMessage("Failed to save changes.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setSyncFilter(newFilter);
      setPage(1);
    }
  };

  const getFilteredItems = () => {
    let items = collectionItems;

    if (syncFilter === "synced") {
      items = items.filter((item) => selectedItemIds.includes(item.id));
    } else if (syncFilter === "unsynced") {
      items = items.filter(
        (item) => !selectedItemIds.includes(item.id) && !pendingItemIds.includes(item.id)
      );
    } else if (syncFilter === "pending") {
      items = items.filter((item) => pendingItemIds.includes(item.id));
    }

    if (searchQuery.trim() !== "") {
      items = items.filter((item) =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase().trim())
      );
    }

    return items;
  };

  const filteredItems = getFilteredItems();
  const syncedCount = collectionItems.filter((item) => selectedItemIds.includes(item.id)).length;
  const unsyncedCount = collectionItems.filter(
    (item) => !selectedItemIds.includes(item.id) && !pendingItemIds.includes(item.id)
  ).length;
  const pendingCount = pendingItemIds.length;

  const startIdx = (page - 1) * rowsPerPage;
  const paginatedItems = filteredItems.slice(startIdx, startIdx + rowsPerPage);
  const leftColumnItems = paginatedItems.slice(0, itemsPerPage);
  const rightColumnItems = paginatedItems.slice(itemsPerPage);

  const getStatusChip = (itemId) => {
    if (pendingItemIds.includes(itemId)) {
      return (
        <Chip
          label="Pending"
          size="small"
          sx={{
            ml: 1,
            backgroundColor: "#ff9800",
            color: "#fff",
            height: "20px",
            fontSize: "0.7rem",
            "& .MuiChip-label": { px: 1, py: 0.5 },
          }}
        />
      );
    }
    return null;
  };

  const isItemDisabled = (itemId) => pendingItemIds.includes(itemId);

  const getDisabledTooltip = (itemId) =>
    pendingItemIds.includes(itemId) ? "This item is currently being synced" : "";

  const renderCheckboxItem = (item) => {
    const isDisabled = isItemDisabled(item.id);
    const tooltipText = getDisabledTooltip(item.id);

    return (
      <Tooltip key={item.id} title={tooltipText} arrow placement="top">
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedItemIds.includes(item.id)}
              onChange={() => handleItemCheck(item.id)}
              disabled={isDisabled}
              sx={{
                color: isDarkMode ? "#a0aec0" : undefined,
                "&.Mui-checked": { color: "#219297" },
                "&.Mui-disabled": { color: isDarkMode ? "#4a5568" : "#bdbdbd" },
              }}
            />
          }
          label={
            <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
              {source === "custom" ? (
                <>
                  <SoftBox display="flex" flexDirection="column">
                    <span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>{item.title}</span>
                    <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
                      {item.url}
                    </SoftTypography>
                  </SoftBox>
                  {getStatusChip(item.id)}
                </>
              ) : (
                <>
                  <span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>{item.title}</span>
                  {getStatusChip(item.id)}
                </>
              )}
            </SoftBox>
          }
          sx={{
            "& .MuiFormControlLabel-label": {
              color: isDarkMode ? "#e2e8f0" : undefined,
              opacity: isDisabled ? 0.6 : 1,
            },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#fff",
        p: isMobile ? 2 : 4,
        borderRadius: "16px",
        boxShadow: isDarkMode
          ? "0px 6px 20px rgba(0, 0, 0, 0.3)"
          : "0px 6px 20px rgba(0, 0, 0, 0.06)",
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
      display="flex"
      flexDirection="column"
      gap={3}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <SoftBox display="flex" alignItems="center" gap={1}>
        <img
          src={AnseraLogo}
          alt="Ansera Logo"
          width={64}
          style={{
            filter: isDarkMode ? "brightness(0) invert(1)" : "none",
            transition: "filter 0.3s ease",
          }}
        />
        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
          Data Syncing for Better Search Results
        </SoftTypography>
      </SoftBox>

      <SoftTypography
        variant="body2"
        sx={{
          borderBottom: `1px solid ${isDarkMode ? "#1a2542" : "#e0e0e0"}`,
          pb: 1,
          color: isDarkMode ? "#a0aec0" : "text.secondary",
        }}
      >
        Please sync your data using the criteria below to enhance accuracy and performance.
      </SoftTypography>

      {/* Content Type + Collection selectors */}
      <SoftBox display="flex" flexDirection={isMobile ? "column" : "row"} gap={2}>
        <SoftBox flex={1}>
          <SoftTypography variant="button" fontWeight="bold" mb={0.5} sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
            Choose Content Type
          </SoftTypography>
          <SoftSelect
            options={[
              { value: "page", label: "Pages" },
              ...(source === "webflow" ? [{ value: "collections", label: "Collections" }] : []),
            ]}
            placeholder="- Select Type -"
            value={
              contentType
                ? { value: contentType, label: contentType.charAt(0).toUpperCase() + contentType.slice(1) }
                : null
            }
            onChange={({ value }) => handleContentTypeChange(value)}
          />
        </SoftBox>

        {source === "webflow" && contentType === "collections" && (
          <SoftBox flex={1}>
            <SoftTypography variant="button" fontWeight="bold" mb={0.5} sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
              Filter by Category
            </SoftTypography>
            <SoftSelect
              options={collections.map((cat) => ({ value: cat.id, label: cat.displayName }))}
              placeholder="- Select Collection -"
              onChange={({ value }) => handleCollectionChange(value)}
            />
          </SoftBox>
        )}
      </SoftBox>

      {(contentType === "page" || selectedCollection) && (
        <SoftBox>
          {/* Row 1: Label + Items per page */}
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            flexWrap="wrap"
            gap={2}
          >
            <SoftTypography
              variant="button"
              fontWeight="bold"
              sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
            >
              Select {contentType === "page" ? "Pages" : "Collection Items"}:
            </SoftTypography>

            <SoftBox sx={{ minWidth: 120 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                mb={0.5}
                display="block"
                sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
              >
                Items per page
              </SoftTypography>
              <SoftSelect
                options={[5, 10, 20, 30, 50, 100, 150].map((v) => ({ value: v, label: String(v) }))}
                value={{ value: itemsPerPage, label: String(itemsPerPage) }}
                onChange={({ value }) => handleItemsPerPageChange(value)}
              />
            </SoftBox>
          </SoftBox>

          {/* Row 2: Filter chips + Search bar inline */}
          <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flex: "0 0 auto" }}>
              {[
                { key: "all", label: `All (${collectionItems.length})`, color: "#219297" },
                { key: "synced", label: `Synced (${syncedCount})`, color: "#4caf50" },
                { key: "unsynced", label: `Unsynced (${unsyncedCount})`, color: "#ff9800" },
                { key: "pending", label: `Pending (${pendingCount})`, color: "#2196f3" },
              ].map(({ key, label, color }) => (
                <Chip
                  key={key}
                  label={label}
                  onClick={() => handleSyncFilterChange(null, key)}
                  sx={{
                    backgroundColor: syncFilter === key ? color : isDarkMode ? "#2d2d3f" : "#f5f5f5",
                    color: syncFilter === key ? "#fff" : isDarkMode ? "#e2e8f0" : undefined,
                    "&:hover": {
                      backgroundColor: syncFilter === key ? color : isDarkMode ? "#3d3d4f" : "#e0e0e0",
                    },
                  }}
                />
              ))}
            </Box>

            {/* Search bar — grows to fill remaining space */}
            <SoftBox sx={{ flex: 1, minWidth: 180 }}>
<TextField
  fullWidth
  size="small"
  placeholder="Search by title..."
  value={searchQuery}
  onChange={(e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon
          sx={{
            color: isDarkMode ? "#a0aec0" : "#9e9e9e",
            fontSize: 20,
          }}
        />
      </InputAdornment>
    ),
  }}
  sx={{
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDarkMode ? "#1a1a2e" : "#f9f9f9",
      borderRadius: "8px",

      "& fieldset": {
        borderColor: isDarkMode ? "#2d3748" : "#e0e0e0",
      },

      "&:hover": {
        backgroundColor: isDarkMode ? "#1a1a2e" : "#f9f9f9",
      },

      "&:hover fieldset": {
        borderColor: "#219297",
      },

      "&.Mui-focused": {
        backgroundColor: isDarkMode ? "#1a1a2e" : "#ffffff",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#219297",
      },
    },

    "& input": {
      color: isDarkMode ? "#e2e8f0" : "#333",
    },

    "& input::placeholder": {
      color: isDarkMode ? "#718096" : "#9e9e9e",
      opacity: 1,
    },

    "& input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 100px ${
        isDarkMode ? "#1a1a2e" : "#f9f9f9"
      } inset`,
      WebkitTextFillColor: isDarkMode ? "#e2e8f0" : "#333",
    },
  }}
/>
              {searchQuery.trim() !== "" && (
                <SoftTypography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block", color: isDarkMode ? "#a0aec0" : "text.secondary" }}
                >
                  {filteredItems.length} result{filteredItems.length !== 1 ? "s" : ""} found for &quot;{searchQuery}&quot;
                </SoftTypography>
              )}
            </SoftBox>
          </SoftBox>

          {loadingItems ? (
            <SoftBox display="flex" alignItems="center" justifyContent="center" py={3}>
              <CircularProgress size={28} sx={{ color: "#219297" }} />
            </SoftBox>
          ) : (
            <>
              {filteredItems.length === 0 ? (
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  py={3}
                  sx={{
                    backgroundColor: isDarkMode ? "#1a1a2e" : "#f9f9f9",
                    borderRadius: "8px",
                  }}
                >
                  <SoftTypography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
                    {searchQuery.trim() !== ""
                      ? `No results found for "${searchQuery}"`
                      : `No ${syncFilter !== "all" ? syncFilter : ""} items found`}
                  </SoftTypography>
                </SoftBox>
              ) : (
                <>
                  <Grid container spacing={isMobile ? 2 : 3}>
                    <Grid item xs={12} md={6}>
                      <FormGroup>{leftColumnItems.map(renderCheckboxItem)}</FormGroup>
                    </Grid>
                    {rightColumnItems.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <FormGroup>{rightColumnItems.map(renderCheckboxItem)}</FormGroup>
                      </Grid>
                    )}
                  </Grid>

                  {filteredItems.length > rowsPerPage && (
                    <SoftBox display="flex" justifyContent="center" mt={2}>
                      <Pagination
                        count={Math.ceil(filteredItems.length / rowsPerPage)}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        sx={{
                          "& .MuiPaginationItem-root": {
                            color: isDarkMode ? "#e2e8f0" : undefined,
                            "&.Mui-selected": {
                              backgroundColor: "#219297",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#1a767a" },
                            },
                          },
                        }}
                      />
                    </SoftBox>
                  )}
                </>
              )}
            </>
          )}
        </SoftBox>
      )}

      {saving && (
        <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary", lineHeight: 2.0 }}>
          Saving changes may take a few minutes.
        </SoftTypography>
      )}

      {(contentType === "page" || selectedCollection) && (
        <SoftBox
          display="flex"
          justifyContent={isMobile ? "center" : "flex-start"}
          flexDirection={isMobile ? "column" : "row"}
          gap={2}
          mt={2}
        >
          <Button
            variant="contained"
            size="medium"
            onClick={handleSaveChanges}
            disabled={saving}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": { backgroundColor: "#1a767a" },
              "&:disabled": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "#cccccc",
                color: isDarkMode ? "#a0aec0" : "#666666",
              },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>

          <Button
            variant="contained"
            size="medium"
            onClick={handleSaveChanges}
            disabled={saving}
            sx={{
              backgroundColor: "#219297",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": { backgroundColor: "#1a767a" },
            }}
          >
            Resync
          </Button>
        </SoftBox>
      )}
    </SoftBox>
  );
};

export default DataSync;